import React from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Icon,
  Image,
  Input,
  Select,
  SimpleGrid,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { FiCheckCircle, FiStar, FiUploadCloud } from "react-icons/fi";
import { TiptapEditor } from "./TiptapEditor";
import { IconToggleCard } from "./IconToggleCard";
import { useAuth } from "../../context/AuthContext";
import { files } from "../../services/api";
import { getNewsContentHtml, getNewsImage } from "../../utils/news";

interface NewsFormProps {
  initialData?: any;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}

const buildInitialFormData = (initialData?: any) => {
  const data = initialData || {};
  const coverImageId =
    typeof data.coverImage === "object" && data.coverImage !== null
      ? data.coverImage._id || ""
      : data.coverImage || "";
  const existingAuthorLabel =
    typeof data.author === "string"
      ? data.author
      : data.author?.fullName || data.author?.mobile || data.author?.nationalId || "";

  return {
    isActive: true,
    isFeatured: false,
    ...data,
    authorMode: data?.authorName ? "manual" : "currentUser",
    authorName: data?.authorName || "",
    existingAuthorLabel,
    coverImage: coverImageId,
    content: getNewsContentHtml(data.content || ""),
    tags: Array.isArray(data.tags) ? data.tags.join(", ") : data.tags || "",
  };
};

const newsCategories = [
  { value: "announcements", label: "اخبار و اطلاعیه‌ها" },
  { value: "multimedia", label: "چندرسانه‌ای" },
  { value: "statistical-reports", label: "گزارش‌های آماری" },
  { value: "financial-reports", label: "گزارش‌های مالی" },
  { value: "publications", label: "انتشارات" },
  { value: "analysis", label: "مقالات و تحلیل‌ها" },
];

const manualNewsAuthors = [
  "علی پازکی",
  "محمد غواصی کناری",
  "حسین صیادلو",
  "علی حیدری",
  "میرسجاد مسجد موسوی",
  "اسماعیل ثروتی",
  "میثم امیری پریان",
  "متین عبدی",
  "عباس ابراهیمی",
  "عباس دادرس",
  "محمدرضا رافع",
  "همایون نسیمی",
];

export const NewsForm: React.FC<NewsFormProps> = ({
  initialData = {},
  onSubmit,
  onCancel,
}) => {
  const { user } = useAuth();
  const toast = useToast();
  const [formData, setFormData] = React.useState<any>(() =>
    buildInitialFormData(initialData),
  );
  const [selectedCoverImageFile, setSelectedCoverImageFile] =
    React.useState<File | null>(null);
  const [coverImagePreviewUrl, setCoverImagePreviewUrl] = React.useState("");
  const [isUploadingCoverImage, setIsUploadingCoverImage] = React.useState(false);

  React.useEffect(() => {
    setFormData(buildInitialFormData(initialData));
    setSelectedCoverImageFile(null);
    setCoverImagePreviewUrl(initialData?.coverImage ? getNewsImage(initialData) : "");
  }, [initialData]);

  React.useEffect(() => {
    return () => {
      if (coverImagePreviewUrl.startsWith("blob:")) {
        window.URL.revokeObjectURL(coverImagePreviewUrl);
      }
    };
  }, [coverImagePreviewUrl]);

  const inputBg = useColorModeValue("white", "#23272F");
  const inputBorder = useColorModeValue("#e5e7eb", "#444");
  const labelColor = useColorModeValue("gray.700", "gray.200");
  const sectionBg = useColorModeValue("gray.50", "whiteAlpha.50");
  const textColor = useColorModeValue("gray.800", "white");
  const currentAuthorLabel =
    user?.fullName || user?.username || user?.mobile || "کاربر جاری";
  const displayedCurrentAuthorLabel =
    formData.existingAuthorLabel || currentAuthorLabel;

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value, type } = e.target;
    const checked =
      type === "checkbox" ? (e.target as HTMLInputElement).checked : undefined;

    setFormData((prev: any) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleCoverImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextFile = e.target.files?.[0] || null;

    if (!nextFile) {
      return;
    }

    if (coverImagePreviewUrl.startsWith("blob:")) {
      window.URL.revokeObjectURL(coverImagePreviewUrl);
    }

    const nextPreviewUrl = window.URL.createObjectURL(nextFile);
    setSelectedCoverImageFile(nextFile);
    setCoverImagePreviewUrl(nextPreviewUrl);
  };

  const handleRemoveCoverImage = () => {
    if (coverImagePreviewUrl.startsWith("blob:")) {
      window.URL.revokeObjectURL(coverImagePreviewUrl);
    }

    setSelectedCoverImageFile(null);
    setCoverImagePreviewUrl("");
    setFormData((prev: any) => ({ ...prev, coverImage: null }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload: any = {
      ...formData,
      tags:
        typeof formData.tags === "string"
          ? formData.tags
              .split(",")
              .map((tag: string) => tag.trim())
              .filter(Boolean)
          : formData.tags,
    };

    if (payload.authorMode !== "manual") {
      payload.authorMode = "currentUser";
      payload.authorName = "";
    }

    try {
      if (selectedCoverImageFile) {
        setIsUploadingCoverImage(true);
        const uploaded = await files.upload(selectedCoverImageFile);
        payload.coverImage = uploaded.file._id;
      }
    } catch (error: any) {
      toast({
        title: "خطا در آپلود تصویر شاخص",
        description:
          error?.response?.data?.message || error?.message || "خطای ناشناخته",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    } finally {
      setIsUploadingCoverImage(false);
    }

    await Promise.resolve(onSubmit(payload));
  };

  const fieldProps = {
    borderRadius: "lg",
    bg: inputBg,
    borderColor: inputBorder,
    boxShadow: "sm",
    fontSize: "md",
  };

  return (
    <form onSubmit={handleSubmit}>
      <VStack spacing={5} align="stretch" w="100%" py="10px">
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <IconToggleCard
            name="isActive"
            label="فعال"
            icon={FiCheckCircle}
            isChecked={Boolean(formData.isActive)}
            onChange={handleChange}
          />
          <IconToggleCard
            name="isFeatured"
            label="منتخب"
            icon={FiStar}
            isChecked={Boolean(formData.isFeatured)}
            onChange={handleChange}
          />
        </SimpleGrid>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <FormControl>
            <FormLabel fontWeight="bold" color={labelColor}>
              نوع نویسنده
            </FormLabel>
            <Select
              {...fieldProps}
              name="authorMode"
              value={formData.authorMode || "currentUser"}
              onChange={handleChange}
            >
              <option value="currentUser">کاربر جاری</option>
              <option value="manual">نویسنده ثابت</option>
            </Select>
            <FormHelperText>
              نویسنده خبر می‌تواند کاربر واردشده یا یکی از نام‌های ثابت باشد.
            </FormHelperText>
          </FormControl>

          <FormControl isDisabled={formData.authorMode !== "manual"}>
            <FormLabel fontWeight="bold" color={labelColor}>
              نام نویسنده
            </FormLabel>
            {formData.authorMode === "manual" ? (
              <Select
                {...fieldProps}
                name="authorName"
                value={formData.authorName || ""}
                onChange={handleChange}
              >
                <option value="">انتخاب نویسنده</option>
                {manualNewsAuthors.map((authorName) => (
                  <option key={authorName} value={authorName}>
                    {authorName}
                  </option>
                ))}
              </Select>
            ) : (
              <Input
                {...fieldProps}
                value={displayedCurrentAuthorLabel}
                isReadOnly
              />
            )}
            <FormHelperText>
              {formData.authorMode === "manual"
                ? "یکی از نویسندگان از پیش تعریف‌شده را انتخاب کنید."
                : "در این حالت نویسنده فعلی خبر نمایش داده می‌شود."}
            </FormHelperText>
          </FormControl>
        </SimpleGrid>

        <FormControl>
          <FormLabel fontWeight="bold" color={labelColor}>
            تصویر شاخص
          </FormLabel>
          <Box
            border="1px dashed"
            borderColor={
              selectedCoverImageFile || formData.coverImage
                ? "green.300"
                : inputBorder
            }
            borderRadius="xl"
            p={4}
            bg={sectionBg}
          >
            <VStack spacing={3}>
              {coverImagePreviewUrl ? (
                <Box
                  overflow="hidden"
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={inputBorder}
                  w="100%"
                >
                  <Image
                    src={coverImagePreviewUrl}
                    alt="cover preview"
                    w="100%"
                    maxH="240px"
                    objectFit="cover"
                  />
                </Box>
              ) : null}
              <Icon
                verticalAlign="middle"
                as={FiUploadCloud as any}
                boxSize={6}
                color={
                  selectedCoverImageFile || formData.coverImage
                    ? "green.500"
                    : "brand.500"
                }
              />
              <Text fontSize="sm" fontWeight="bold" color={textColor}>
                {selectedCoverImageFile?.name ||
                  (formData.coverImage
                    ? "تصویر شاخص فعلی انتخاب شده است."
                    : "تصویر شاخص خبر را بارگذاری کنید")}
              </Text>
              <Button
                as="label"
                htmlFor="news-cover-image"
                size="sm"
                colorScheme="brand"
                variant={
                  selectedCoverImageFile || formData.coverImage
                    ? "outline"
                    : "solid"
                }
                cursor="pointer"
              >
                انتخاب تصویر
              </Button>
              <Input
                id="news-cover-image"
                type="file"
                accept="image/*"
                display="none"
                onChange={handleCoverImageChange}
              />
              <FormHelperText m={0}>
                تصویر شاخص از طریق فایل آپلود می‌شود و در خبر ذخیره خواهد شد.
              </FormHelperText>
              {coverImagePreviewUrl || formData.coverImage ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  colorScheme="red"
                  onClick={handleRemoveCoverImage}
                >
                  حذف تصویر
                </Button>
              ) : null}
            </VStack>
          </Box>
        </FormControl>

        <FormControl isRequired>
          <FormLabel fontWeight="bold" color={labelColor}>
            عنوان
          </FormLabel>
          <Input
            name="title"
            value={formData.title || ""}
            onChange={handleChange}
            {...fieldProps}
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel fontWeight="bold" color={labelColor}>
            دسته‌بندی
          </FormLabel>
          <Select
            name="category"
            value={formData.category || ""}
            onChange={handleChange}
            {...fieldProps}
          >
            <option value="">انتخاب دسته‌بندی</option>
            {newsCategories.map((category) => (
              <option key={category.value} value={category.value}>
                {category.label}
              </option>
            ))}
          </Select>
        </FormControl>

        <FormControl>
          <FormLabel fontWeight="bold" color={labelColor}>
            خلاصه
          </FormLabel>
          <Textarea
            name="excerpt"
            value={formData.excerpt || ""}
            onChange={handleChange}
            rows={3}
            {...fieldProps}
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel fontWeight="bold" color={labelColor}>
            متن خبر
          </FormLabel>
          <TiptapEditor
            value={formData.content || ""}
            onChange={(content) =>
              setFormData((prev: any) => ({ ...prev, content }))
            }
            placeholder="متن خبر را اینجا بنویسید..."
          />
          <FormHelperText>
            ویرایشگر به صورت کامل محلی اجرا می‌شود و هیچ وابستگی به CDN یا سرویس
            ابری ندارد.
          </FormHelperText>
        </FormControl>

        <FormControl>
          <FormLabel fontWeight="bold" color={labelColor}>
            برچسب‌ها
          </FormLabel>
          <Input
            name="tags"
            value={formData.tags || ""}
            onChange={handleChange}
            placeholder="با کاما جدا کنید"
            {...fieldProps}
          />
        </FormControl>

        <FormControl>
          <FormLabel fontWeight="bold" color={labelColor}>
            عنوان سئو
          </FormLabel>
          <Input
            name="seoTitle"
            value={formData.seoTitle || ""}
            onChange={handleChange}
            {...fieldProps}
          />
        </FormControl>

        <FormControl>
          <FormLabel fontWeight="bold" color={labelColor}>
            توضیحات سئو
          </FormLabel>
          <Textarea
            name="seoDescription"
            value={formData.seoDescription || ""}
            onChange={handleChange}
            rows={3}
            {...fieldProps}
          />
        </FormControl>

        <HStack justify="end" pt={2}>
          <Button type="button" variant="ghost" onClick={onCancel}>
            لغو
          </Button>
          <Button type="submit" colorScheme="brand" isLoading={isUploadingCoverImage}>
            ارسال
          </Button>
        </HStack>
      </VStack>
    </form>
  );
};
